import React from "react";
import styled, { keyframes } from "styled-components";
import {
  C,
  SEMI,
  REG,
  SegLabel,
  Section,
  FieldBlock,
  FloatLabel,
  ErrorLine,
} from "../Styles";

function maskPhone(phone) {
  if (!phone) return null;
  const d = phone.replace(/\D/g, "");
  if (d.length < 10) return phone;
  return `+91 ${d.slice(0, 2)}\u2022\u2022\u2022\u2022\u2022${d.slice(-3)}`;
}

const StepOtp = ({ values, handleChange, handleBlur, errors, touched }) => {
  return (
    <>
      {/* ── Destination info card ── */}
      <Section $delay="0s">
        <OtpInfoCard>
          <OtpInfoIcon>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 1l3.5 7 7.5 1-5.5 5.5 1.5 7.5L12 19l-7 3.5 1.5-7.5L1 9.5l7.5-1z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            </svg>
          </OtpInfoIcon>
          <div>
            <OtpInfoTitle>
              Check your {values.otpService === "email" ? "inbox" : "messages"}
            </OtpInfoTitle>
            <OtpInfoDesc>
              A 6-digit verification code was sent to{" "}
              <strong>
                {values.otpService === "email"
                  ? values.email || "your email"
                  : maskPhone(values.phone) || "your phone"}
              </strong>
            </OtpInfoDesc>
          </div>
        </OtpInfoCard>
      </Section>

      {/* ── OTP input ── */}
      <Section $delay="0.05s">
        <SegLabel>Enter verification code</SegLabel>
        <OtpInputWrap>
          <FieldBlock>
            <FloatLabel>6-Digit OTP</FloatLabel>
            <OtpInput
              name="otp"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={values.otp}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="— — — — — —"
              $err={touched.otp && !!errors.otp}
            />
            {touched.otp && errors.otp && <ErrorLine>{errors.otp}</ErrorLine>}
          </FieldBlock>
          <OtpHint>
            <OtpHintDot />
            Code expires in 10 minutes. Didn't receive it?{" "}
            <ResendLink type="button">Resend OTP</ResendLink>
          </OtpHint>
        </OtpInputWrap>
      </Section>
    </>
  );
};

export default StepOtp;

/* ─── Styled components ──────────────────────────────────────────────────── */

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0,131,140,0.3); }
  50%       { box-shadow: 0 0 0 8px rgba(0,131,140,0); }
`;

const OtpInfoCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 20px;
  border-radius: 14px;
  background: linear-gradient(135deg, ${C.tealAlpha}, rgba(0, 131, 140, 0.04));
  border: 1.5px solid ${C.tealLine};
`;

const OtpInfoIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${C.teal};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  animation: ${pulse} 2.5s ease-in-out infinite;
`;

const OtpInfoTitle = styled.div`
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 14px;
  color: ${C.navy};
  margin-bottom: 4px;
`;

const OtpInfoDesc = styled.div`
  font-family: ${REG};
  font-size: 13px;
  color: ${C.textSub};
  line-height: 1.6;

  strong {
    font-family: ${SEMI};
    color: ${C.navy};
  }
`;

const OtpInputWrap = styled.div`
  max-width: 360px;
`;

const OtpInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  border-radius: 12px;
  border: 1.5px solid ${(p) => (p.$err ? C.error : C.teal)};
  background: ${C.surface};
  font-family: ${SEMI};
  font-size: 24px;
  font-weight: 800;
  color: ${C.navy};
  letter-spacing: 0.35em;
  text-align: center;
  outline: none;
  transition: all 0.22s;
  box-sizing: border-box;
  box-shadow: 0 0 0 3px ${(p) => (p.$err ? C.errorPale : C.tealAlpha)};

  &::placeholder {
    color: ${C.mutedLight};
    letter-spacing: 0.2em;
    font-size: 18px;
  }
  &:focus {
    border-color: ${(p) => (p.$err ? C.error : C.tealDark)};
    background: ${C.white};
    box-shadow: 0 0 0 4px ${(p) => (p.$err ? C.errorPale : C.tealAlpha)};
  }
`;

const OtpHint = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  font-family: ${REG};
  font-size: 12px;
  color: ${C.muted};
`;

const OtpHintDot = styled.div`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${C.teal};
  flex-shrink: 0;
`;

const ResendLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-family: ${SEMI};
  font-size: 12px;
  font-weight: 700;
  color: ${C.teal};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${C.tealDark};
  }
`;
